import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function Appel({ classId, canEdit }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [demi, setDemi] = useState("matin");
  const [students, setStudents] = useState([]);
  const [existing, setExisting] = useState([]);
  const [absent, setAbsent] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!classId) return;
    try {
      const [s, a] = await Promise.all([api.get("/students", { params: { class_id: classId } }), api.get("/absences", { params: { class_id: classId } })]);
      const sorted = [...s.data].sort((x, y) => (x.nom || "").localeCompare(y.nom || "", "fr") || (x.prenom || "").localeCompare(y.prenom || "", "fr"));
      const ex = a.data.filter((x) => x.date === date && x.demi_journee === demi && !x.retard);
      setStudents(sorted); setExisting(ex); setAbsent(new Set(ex.map((x) => x.student_id)));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classId, date, demi]);

  const toggle = (id) => { if (!canEdit) return; setAbsent((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const presents = students.length - absent.size;
  const dirty = useMemo(() => {
    const ex = new Set(existing.map((x) => x.student_id));
    return ex.size !== absent.size || [...absent].some((id) => !ex.has(id));
  }, [existing, absent]);

  const save = async () => {
    setBusy(true);
    try {
      const exIds = new Set(existing.map((x) => x.student_id));
      await Promise.all([
        ...[...absent].filter((id) => !exIds.has(id)).map((id) => api.post("/absences", { student_id: id, date, demi_journee: demi })),
        ...existing.filter((x) => !absent.has(x.student_id)).map((x) => api.delete(`/absences/${x.id}`)),
      ]);
      toast.success(`Appel enregistré : ${presents} présent(s), ${absent.size} absent(s)`);
      load();
    } catch (e) { toast.error(formatError(e)); load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="appel-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="appel-date" />
        <Select value={demi} onValueChange={setDemi}>
          <SelectTrigger className="w-40" data-testid="appel-demi"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="matin">Matin</SelectItem><SelectItem value="apres-midi">Après-midi</SelectItem></SelectContent>
        </Select>
        <div className="flex gap-3 text-sm ml-auto">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#1F7A63]" />{presents} présent(s)</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />{absent.size} absent(s)</span>
        </div>
      </div>
      {!canEdit && <p className="text-xs text-[#5C736A]">Consultation uniquement : l&apos;appel est réalisé par l&apos;enseignant de la classe.</p>}
      <div className="ek-card p-0 divide-y divide-[#E2E8F0]">
        {students.map((s) => {
          const isAbs = absent.has(s.id);
          return (
            <button type="button" key={s.id} onClick={() => toggle(s.id)} disabled={!canEdit} data-testid={`appel-row-${s.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isAbs ? "bg-red-50" : "hover:bg-[#F6F8F9]"}`}>
              <span className={`w-8 h-8 rounded-full grid place-items-center text-white ${isAbs ? "bg-red-500" : "bg-[#1F7A63]"}`}>{isAbs ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}</span>
              <span className="flex-1 min-w-0"><span className="font-normal">{s.prenom}</span> <span className="font-semibold">{s.nom}</span></span>
              <span className={`text-xs font-semibold ${isAbs ? "text-red-600" : "text-[#1F7A63]"}`}>{isAbs ? "Absent" : "Présent"}</span>
            </button>
          );
        })}
        {!students.length && <div className="p-8 text-center text-sm text-[#5C736A]">Aucun élève dans cette classe.</div>}
      </div>
      {canEdit && (
        <div className="sticky bottom-3 flex justify-end">
          <Button onClick={save} disabled={!dirty || busy} className="h-12 px-6 bg-[#1F7A63] hover:bg-[#165c4b] text-white shadow-lg" data-testid="appel-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer l&apos;appel
          </Button>
        </div>
      )}
    </div>
  );
}
